/* Joy: progressively enhanced native Shopify forms. No third-party runtime. */
(() => {
  'use strict';
  // routes.root_url is "/" or "/en"; end it with a slash so root + 'cart' works in every language.
  const root = (document.body.dataset.root || '/').replace(/\/?$/, '/');
  // Messages in the store's language, rendered by snippets/joy-strings.liquid.
  const T = window.JoyStrings || {};
  // One cart lock shared with joy-product.js, so the drawer and "Sepete Ekle" never overlap.
  const cart = { busy: false };
  const announce = (message) => {
    const region = document.querySelector('[data-announcer]');
    if (region) region.textContent = message;
  };
  const maxQuantity = Number(window.JoySettings?.maxQuantity) || 5;
  // Only a failed network request is a lost connection; everything else shows Shopify's own message.
  class ConnectionError extends Error {}
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    let response;
    try {
      response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
    } catch (failure) {
      throw new ConnectionError(T.connection_lost);
    } finally { clearTimeout(timeout); }
    let data;
    try { data = await response.json(); } catch (failure) { throw new Error(T.generic_error); }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : T.generic_error);
    return data;
  };
  const parsed = (html) => new DOMParser().parseFromString(html, 'text/html');
  // Re-render the drawer from the server so it always shows the real cart after an error.
  const refreshCart = async () => {
    try {
      const sections = await request(root + '?sections=joy-cart-drawer');
      updateCart({ sections }, true);
    } catch (failure) { /* keep the current drawer */ }
  };
  const updateCart = (data, shouldOpen = true) => {
    const html = data.sections?.['joy-cart-drawer'];
    const next = html && parsed(html).querySelector('joy-cart');
    const previous = document.querySelector('joy-cart');
    if (!next || !previous) {
      window.location.assign(root + 'cart');
      return;
    }
    const scrollPosition = previous.querySelector('.drawer-items')?.scrollTop || 0;
    const focused = previous.contains(document.activeElement) ? document.activeElement : null;
    const focusKey = focused?.dataset.key;
    const focusLabel = focused?.getAttribute('aria-label');
    previous.querySelector('dialog')?.close();
    previous.replaceWith(next);
    const count = Number(next.dataset.itemCount || 0);
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
    if (shouldOpen) {
      next.open();
      const items = next.querySelector('.drawer-items');
      if (items) items.scrollTop = scrollPosition;
      if (focusKey) [...next.querySelectorAll('[data-cart-change]')].find((button) => button.dataset.key === focusKey && button.getAttribute('aria-label') === focusLabel)?.focus({ preventScroll: true });
    }
    announce(String(T.cart_updated || '').replace('[count]', count));
    fixCart();
  };
  // Service lines follow their piece: snippets/joy-cart-links.liquid (in the drawer) lists what is off, applied here
  // after every cart change and on page load. The same list twice in a row is not applied again (remembered for the
  // tab, as the cart page reloads after a fix).
  let lastFix = '';
  try { lastFix = sessionStorage.getItem('joy-cart-fix') || ''; } catch (failure) { /* storage blocked */ }
  const fixCart = async () => {
    const script = document.querySelector('joy-cart [data-cart-fix]');
    if (!script || script.textContent === lastFix) return;
    // Called from inside a cart change (updateCart): wait until that change has released the cart.
    if (cart.busy) {
      setTimeout(fixCart, 300);
      return;
    }
    lastFix = script.textContent;
    try { sessionStorage.setItem('joy-cart-fix', lastFix); } catch (failure) { /* storage blocked */ }
    let fix;
    try { fix = JSON.parse(lastFix); } catch (failure) { return; }
    const post = (path, body) => request(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: ['joy-cart-drawer'], sections_url: root + 'cart' })
    });
    cart.busy = true;
    let data = null;
    try {
      if (Object.keys(fix.updates || {}).length) data = await post('cart/update.js', { updates: fix.updates });
      for (const line of fix.strip || []) data = await post('cart/change.js', line);
    } catch (failure) { /* the next page shows the cart as it is */ } finally { cart.busy = false; }
    if (!data) return;
    // The cart page lists the lines itself: reload it; elsewhere the drawer is refreshed quietly.
    if (document.querySelector('.cart-page')) window.location.reload();
    else updateCart(data, false);
  };

  // Helpers for page scripts loaded after this file (assets/joy-product.js).
  window.Joy = { root, T, cart, announce, request, parsed, updateCart, ConnectionError, maxQuantity };
  fixCart();

  // Rotates automatically; pauses while hovered, focused, hidden or selected in the theme editor.
  class JoyAnnouncement extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      clearTimeout(this.timer);
      this.slides = [...this.querySelectorAll('[data-announcement-message]')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.hovered = false;
      this.focused = false;
      this.editorSelection = false;
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(3000, Math.min(12000, Number(this.dataset.interval) || 4000));
      this.live = this.querySelector('[data-announcement-live]');
      const options = { signal: this.controller.signal };
      // Arrows: the chosen message is read out (polite); automatic changes stay silent.
      this.addEventListener('click', (event) => {
        const arrow = event.target.closest('[data-announcement-step]');
        if (!arrow) return;
        if (this.live) this.live.setAttribute('aria-live', 'polite');
        this.show(this.index + Number(arrow.dataset.announcementStep));
        this.schedule();
      }, options);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!this.isConnected) return;
          this.focused = this.contains(document.activeElement);
          this.schedule();
        });
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.editorSelection = true;
        this.show(index);
        this.schedule();
      }, options);
      document.addEventListener('shopify:block:deselect', (event) => {
        if (!this.slides.some((slide) => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorSelection = false;
        this.schedule();
      }, options);
      this.show(0);
      this.schedule();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
    }
    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        const inactive = position !== this.index;
        slide.setAttribute('aria-hidden', String(inactive));
        slide.inert = inactive;
      });
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.hovered || this.focused || this.editorSelection || document.hidden) return;
      this.timer = setTimeout(() => {
        if (this.live) this.live.setAttribute('aria-live', 'off');
        this.show(this.index + 1);
        this.schedule();
      }, this.interval);
    }
  }

  class JoyCart extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('click', async (event) => {
        const button = event.target.closest('button');
        if (button?.matches('[data-close-cart]')) this.querySelector('dialog').close();
        if (button?.matches('[data-cart-change]') && !this.busy && !cart.busy) {
          this.busy = true;
          cart.busy = true;
          const error = this.querySelector('[data-cart-error]');
          error.hidden = true;
          this.querySelectorAll('button').forEach((control) => {
            control.dataset.wasDisabled = String(control.disabled);
            control.disabled = true;
          });
          this.setAttribute('aria-busy', 'true');
          try {
            // A piece with an HRD report changes both lines in one request (snippets/joy-cart-items.liquid).
            const quantity = Number(button.dataset.quantity);
            const linked = button.dataset.linked;
            const data = await request(root + (linked ? 'cart/update.js' : 'cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                ...(linked ? { updates: { [button.dataset.key]: quantity, [linked]: quantity } } : { id: button.dataset.key, quantity }),
                sections: ['joy-cart-drawer'],
                sections_url: root + 'cart'
              })
            });
            updateCart(data);
          } catch (failure) {
            const message = failure instanceof ConnectionError ? T.cart_connection : failure.message;
            await refreshCart();
            const current = document.querySelector('joy-cart [data-cart-error]');
            if (current) { current.textContent = message; current.hidden = false; }
          } finally {
            this.busy = false;
            cart.busy = false;
            this.removeAttribute('aria-busy');
            this.querySelectorAll('button').forEach((control) => { control.disabled = control.dataset.wasDisabled === 'true'; });
          }
        }
      }, { signal: this.controller.signal });
      const dialog = this.querySelector('dialog');
      dialog?.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right) dialog.close();
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
    open() {
      const dialog = this.querySelector('dialog');
      if (dialog && !dialog.open) dialog.showModal();
    }
  }

  // Top search panel with Shopify predictive search results.
  class JoySearch extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.defaultContent = this.results.innerHTML;
      const options = { signal: this.controller.signal };
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 250);
      }, options);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-close-search]')) this.close();
      }, options);
      // Close on a backdrop tap only. On phones the panel fills the screen, so a tap on its
      // blank area below the content (e.g. to hide the keyboard) must not close search.
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const content = this.dialog.querySelector('.search-dialog-inner')?.getBoundingClientRect();
        const panel = this.dialog.getBoundingClientRect();
        if (content && event.clientY > content.bottom && event.clientY <= panel.bottom) return;
        this.close();
      }, options);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.fetchController?.abort();
      this.controller?.abort();
    }
    open() {
      if (!this.dialog.open) this.dialog.showModal();
      this.input.focus();
      this.input.select();
    }
    close() { this.dialog.close(); }
    async search() {
      const terms = this.input.value.trim();
      this.fetchController?.abort();
      if (terms.length < 2) {
        this.results.innerHTML = this.defaultContent;
        return;
      }
      this.fetchController = new AbortController();
      const url = new URL(root + 'search/suggest', location.origin);
      url.searchParams.set('q', terms);
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'joy-predictive-search');
      this.results.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: this.fetchController.signal });
        if (!response.ok) throw new Error('Search failed');
        const content = parsed(await response.text()).querySelector('[data-predictive]');
        if (content && this.input.value.trim() === terms) this.results.replaceChildren(content);
      } catch (failure) {
        if (failure.name !== 'AbortError') this.results.innerHTML = this.defaultContent;
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('joy-search')) customElements.define('joy-search', JoySearch);
  if (!customElements.get('joy-announcement')) customElements.define('joy-announcement', JoyAnnouncement);
  if (!customElements.get('joy-cart')) customElements.define('joy-cart', JoyCart);

  document.addEventListener('click', (event) => {
    const searchLink = event.target.closest('[data-open-search]');
    if (searchLink && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
      const search = document.querySelector('joy-search');
      if (search?.querySelector('dialog')?.showModal) { event.preventDefault(); search.open(); }
    }
    const cartLink = event.target.closest('[data-open-cart]');
    if (cartLink && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
      const drawer = document.querySelector('joy-cart');
      if (drawer?.querySelector('dialog')?.showModal) { event.preventDefault(); drawer.open(); }
    }
  });
  // Cart approval: saved on the cart as soon as it is ticked (survives drawer re-renders);
  // an unticked box shows an inline prompt instead of the browser bubble.
  document.addEventListener('change', (event) => {
    const box = event.target.closest('[data-cart-consent] input[type="checkbox"]');
    if (!box) return;
    const wrap = box.closest('[data-cart-consent]');
    wrap.classList.remove('is-invalid');
    wrap.querySelector('[data-consent-error]').hidden = true;
    fetch(root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [box.dataset.consentLabel]: box.checked ? box.value : '' } })
    }).catch(() => {});
  });
  document.addEventListener('invalid', (event) => {
    const box = event.target.closest?.('[data-cart-consent] input[type="checkbox"]');
    if (!box) return;
    event.preventDefault();
    const wrap = box.closest('[data-cart-consent]');
    wrap.classList.add('is-invalid');
    wrap.querySelector('[data-consent-error]').hidden = false;
    box.focus({ preventScroll: true });
    // Box and message together, so the drawer's pinned "Ödemeye Geç" never covers the message.
    wrap.scrollIntoView({ block: 'nearest' });
  }, true);
  // "Adetleri Güncelle" (and Enter in a quantity field) skips form validation so the approval box
  // does not block it; the quantity limits (min, max, step) are checked here instead.
  document.addEventListener('submit', (event) => {
    // An HRD report line follows its piece's quantity on every cart submit, also "Ödemeye Geç", which posts the
    // quantities and goes straight to checkout (no page load for the cart's own tidy-up).
    event.target.querySelectorAll?.('[data-hrd-follows]').forEach((report) => {
      const piece = event.target.querySelector(`[data-hrd-token="${CSS.escape(report.dataset.hrdFollows)}"]`);
      if (piece) report.value = piece.value;
    });
    if (event.submitter?.name !== 'update') return;
    const invalid = [...event.target.querySelectorAll('.cart-quantity')].find((input) => !input.checkValidity());
    if (!invalid) return;
    event.preventDefault();
    invalid.reportValidity();
  });
  // Gift note: saved on the cart while typing, so drawer re-renders keep it.
  let noteTimer;
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value })
      }).then((response) => { if (response.ok) announce(T.gift_note_saved); }).catch(() => {});
    }, 600);
  });
  // Favourites: product handles kept in this browser. Hearts on cards and product pages, the header
  // count and the Favorilerim page. Adds and removals are published as Shopify customer events
  // (joy_wishlist_add, joy_wishlist_remove, joy_wishlist_share) for analytics pixels; see docs/ANALYTICS.md.
  const WISHLIST_KEY = 'joy-wishlist';
  const readWishlist = () => {
    try {
      const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
    } catch (error) {
      return [];
    }
  };
  const writeWishlist = (list) => {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, 60)));
    } catch (error) {
      // Storage blocked (private mode): the hearts still toggle for this page view.
    }
  };
  const publishWishlist = (name, data) => {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (error) {
      // Analytics must never break the page.
    }
  };
  const syncWishlist = (scope = document) => {
    const list = readWishlist();
    scope.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const saved = list.includes(button.dataset.handle);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', saved ? T.wishlist_remove : T.wishlist_add);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = list.length;
      count.hidden = list.length === 0;
    });
  };
  const wishlistPage = document.querySelector('[data-wishlist-page]');
  const updateWishlistPage = () => {
    if (!wishlistPage) return;
    const cards = [...wishlistPage.querySelectorAll('[data-wishlist-grid] .product-card')];
    wishlistPage.querySelector('[data-wishlist-empty]').hidden = cards.length > 0;
    wishlistPage.querySelector('[data-wishlist-actions]').hidden = cards.length === 0;
    const lines = cards.map((card) => {
      const link = card.querySelector('h3 a');
      return `• ${link.textContent.trim()}: ${link.href}`;
    }).join('\n');
    const share = wishlistPage.querySelector('[data-wishlist-share]');
    const ask = wishlistPage.querySelector('[data-wishlist-ask]');
    if (share) share.href = `https://wa.me/?text=${encodeURIComponent(`${T.wishlist_share_message}\n${lines}`)}`;
    if (ask) ask.href = `https://wa.me/${wishlistPage.dataset.whatsapp}?text=${encodeURIComponent(`${T.wishlist_ask_message}\n${lines}`)}`;
  };
  const renderWishlistPage = async () => {
    if (!wishlistPage) return;
    const grid = wishlistPage.querySelector('[data-wishlist-grid]');
    // Six requests at a time (a long list would otherwise open up to 60 at once); results keep the list order.
    const handles = readWishlist();
    const results = new Array(handles.length);
    let next = 0;
    const loadCard = async (handle) => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=joy-wishlist-card`);
        if (response.status === 404) return { handle, gone: true };
        if (!response.ok) return { handle };
        // A deleted or unpublished product answers 200 with an empty section (joy-wishlist-card renders a card for
        // every existing product).
        const card = parsed(await response.text()).querySelector('.product-card');
        return card ? { handle, card } : { handle, gone: true };
      } catch (error) {
        return { handle };
      }
    };
    await Promise.all(Array.from({ length: Math.min(6, handles.length) }, async () => {
      while (next < handles.length) {
        const index = next;
        next += 1;
        results[index] = await loadCard(handles[index]);
      }
    }));
    // Products that were deleted or unpublished leave the list.
    const gone = results.filter((result) => result.gone).map((result) => result.handle);
    if (gone.length) writeWishlist(readWishlist().filter((handle) => !gone.includes(handle)));
    grid.replaceChildren(...results.filter((result) => result.card).map((result) => result.card));
    wishlistPage.querySelector('[data-wishlist-loading]').hidden = true;
    syncWishlist();
    updateWishlistPage();
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (button) {
      event.preventDefault();
      const { handle } = button.dataset;
      const list = readWishlist();
      const adding = !list.includes(handle);
      writeWishlist(adding ? [handle, ...list] : list.filter((item) => item !== handle));
      syncWishlist();
      announce(adding ? T.wishlist_added : T.wishlist_removed);
      publishWishlist(adding ? 'joy_wishlist_add' : 'joy_wishlist_remove', {
        product_id: button.dataset.id,
        handle,
        title: button.dataset.title,
        product_type: button.dataset.type,
        price: Number(button.dataset.price),
        currency: window.Shopify?.currency?.active || window.JoySettings?.currency
      });
      if (!adding && wishlistPage?.contains(button)) {
        button.closest('.product-card')?.remove();
        updateWishlistPage();
      }
      return;
    }
    if (event.target.closest('[data-wishlist-share], [data-wishlist-ask]')) {
      publishWishlist('joy_wishlist_share', {
        channel: event.target.closest('[data-wishlist-ask]') ? 'whatsapp_store' : 'whatsapp_share',
        count: readWishlist().length
      });
    }
  });
  // Keep other open tabs in step.
  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) syncWishlist();
  });
  document.addEventListener('shopify:section:load', (event) => syncWishlist(event.target));
  // Collection filters swap their cards in place (joy-collection.js): their hearts are synced there.
  window.Joy.syncWishlist = syncWishlist;
  // WhatsApp contacts (ads plan 2026-10-08): every press of a button that writes to the store's WhatsApp is published
  // as the Shopify customer event joy_whatsapp_click (the "Joy WhatsApp" custom pixel passes it to GA4 and Google Ads
  // as a lead), and a visit that came from a campaign ends its message with a source code: "Ref: G" Google, M Meta,
  // P Pinterest, A an AI assistant (ChatGPT …), O another campaign, so a WhatsApp sale can be traced. The code is read
  // from the landing page's address and kept for the rest of the visit only with marketing consent. See
  // docs/ANALYTICS.md.
  // Shopify's Customer Privacy API, loaded once per page for the source code below, the ad log and the own cookie bar:
  // since Shopify's banner was switched off (2026-10-23) nothing else loads it.
  const privacyReady = new Promise((resolve) => {
    if (!window.Shopify?.loadFeatures) {
      resolve(null);
      return;
    }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
      resolve(error ? null : window.Shopify?.customerPrivacy || null);
    });
  });
  const REF_KEY = 'joy-ref';
  const marketingAllowed = () => {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.marketingAllowed?.());
    } catch (error) {
      return false;
    }
  };
  // AI assistants (owner 2026-10-31: a design lead came with "Ref: O", ChatGPT's utm_source=chatgpt.com) get their own
  // letter, from the link's utm_source or, without one, the landing page's referrer.
  const AI_SOURCE = /chatgpt|openai|perplexity|copilot|gemini\.google|bard|claude|you\.com|phind/;
  const landingRef = (() => {
    const params = new URLSearchParams(location.search);
    const source = (params.get('utm_source') || '').toLowerCase();
    let referrer = '';
    try {
      referrer = new URL(document.referrer).hostname.toLowerCase();
    } catch (error) {
      referrer = '';
    }
    if (params.has('gclid') || params.has('gbraid') || params.has('wbraid') || source.includes('google')) return 'G';
    if (params.has('fbclid') || /facebook|instagram|^fb$|^ig$|^meta$/.test(source)) return 'M';
    if (params.has('epik') || source.includes('pinterest')) return 'P';
    if (AI_SOURCE.test(source) || AI_SOURCE.test(referrer)) return 'A';
    return source ? 'O' : '';
  })();
  const keepRef = () => {
    if (!landingRef || !marketingAllowed()) return;
    try {
      sessionStorage.setItem(REF_KEY, landingRef);
    } catch (error) {
      // Storage blocked: the code still goes with messages sent from this page.
    }
  };
  keepRef();
  document.addEventListener('visitorConsentCollected', keepRef);
  privacyReady.then(keepRef);
  // Paid-click check (owner 2026-10-21) and visit depth (owner 2026-10-23: "handle it also deep behaviour"). A visit
  // from a Google ad (gclid / gbraid / wbraid in the address) tells the store's click log that the page opened, the
  // first touch, WhatsApp to the store, the bag and checkout, and on leaving the page how far it was scrolled, how
  // long it was in view and how often it was tapped. That part needs no cookies and stays on the landing page, so
  // clicks that never reach a person can be found and every campaign's real interest shows even when cookies are
  // declined. With analytics consent the ad visit is kept for the visit (sessionStorage), each later page, option,
  // gallery, video, product card, filter and "Devamı" is reported, and a random visitor number with the ad's first day
  // is kept on the phone (localStorage), so a later visit from any source is logged as a come-back with the days in
  // between. The log (tools/tiklama-kaydi) keeps rows 30 days. See docs/ANALYTICS.md.
  const CLICK_LOG = 'https://joy-ziyaret.tiklama-kaydi.workers.dev/v';
  const AD_KEY = 'joy-ad';
  const FIRST_KEY = 'joy-ad-first';
  const VISIT_KEY = 'joy-ad-visit';
  const analyticsAllowed = () => {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.());
    } catch (error) {
      return false;
    }
  };
  const readStore = (area, key) => {
    try {
      return JSON.parse(window[area].getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  };
  const writeStore = (area, key, value) => {
    try {
      window[area].setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage blocked: only this page reports.
    }
  };
  const landingAd = (() => {
    const params = new URLSearchParams(location.search);
    const name = ['gclid', 'gbraid', 'wbraid'].find((key) => params.get(key));
    return name ? { name, id: params.get(name), campaign: params.get('utm_campaign') || '' } : null;
  })();
  let adClick = landingAd;
  let deep = false; // analytics consent: report the steps beyond the landing page
  const adReport = (stage, extra = {}) => {
    if (!adClick || !navigator.sendBeacon) return;
    const data = new URLSearchParams({
      s: stage,
      k: adClick.name,
      g: adClick.id,
      c: adClick.campaign,
      p: location.pathname,
      ms: String(Math.round(performance.now())),
      wd: navigator.webdriver ? '1' : '0',
      tp: String(navigator.maxTouchPoints || 0),
      w: String(window.innerWidth),
      ...extra
    });
    try {
      navigator.sendBeacon(`${CLICK_LOG}?${data}`);
    } catch (error) {
      // Log unreachable: the visit goes on as usual.
    }
  };
  // The visitor's cookie choice at the moment of the report (owner 2026-10-31: "probably nobody accepts cookies, we are
  // blind"): "cs:" + analytics + marketing as 1/0, "u" while still undecided. Only the choice, nothing about the person;
  // the report turns it into the share of ad visitors whose later steps can be followed.
  const consentCode = () => {
    const choice = (() => {
      try {
        return window.Shopify?.customerPrivacy?.currentVisitorConsent?.() || {};
      } catch (error) {
        return {};
      }
    })();
    const undecided = !choice.analytics && !choice.marketing;
    return `cs:${analyticsAllowed() ? 1 : 0}${marketingAllowed() ? 1 : 0}${undecided ? 'u' : ''}`;
  };
  const step = (() => {
    const sent = new Set();
    return (stage, detail = '') => {
      if (!deep || !adClick || sent.has(`${stage}|${detail}`)) return;
      sent.add(`${stage}|${detail}`);
      adReport(stage, detail ? { x: detail.slice(0, 160) } : {});
    };
  })();
  const arrivedFrom = () => {
    if (landingAd) return 'reklam';
    const source = new URLSearchParams(location.search).get('utm_source');
    if (source) return source.slice(0, 40);
    try {
      const host = new URL(document.referrer).hostname.replace(/^www\./, '');
      if (host && !host.endsWith(location.hostname.replace(/^www\./, ''))) return host.slice(0, 40);
    } catch (error) {
      // No referrer: typed, bookmarked or an app.
    }
    return 'direkt';
  };
  const consented = () => {
    if (deep || !analyticsAllowed()) return;
    deep = true;
    if (landingAd) writeStore('sessionStorage', AD_KEY, landingAd);
    else adClick = adClick || readStore('sessionStorage', AD_KEY);
    const first = readStore('localStorage', FIRST_KEY);
    const newVisit = !readStore('sessionStorage', VISIT_KEY);
    writeStore('sessionStorage', VISIT_KEY, 1);
    if (!first && landingAd) {
      const number = window.crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
      writeStore('localStorage', FIRST_KEY, { ...landingAd, v: number, at: Date.now() });
    } else if (first && newVisit) {
      // A new visit (half an hour or more later) of a phone that once came from an ad: a come-back.
      const days = (Date.now() - first.at) / 864e5;
      if (days >= 0.02) {
        const current = adClick;
        adClick = first;
        adReport('return', { d: days.toFixed(2), v: first.v, x: `${arrivedFrom()} ${current?.campaign || ''}`.trim() });
        adClick = current || first;
      }
    }
    if (!landingAd && adClick) step('page', location.pathname);
  };
  privacyReady.then(consented);
  document.addEventListener('visitorConsentCollected', consented);
  if (landingAd) {
    adReport('load');
    const signs = ['pointerdown', 'scroll', 'keydown'];
    const engaged = () => {
      signs.forEach((type) => window.removeEventListener(type, engaged, true));
      adReport('engage');
    };
    signs.forEach((type) => window.addEventListener(type, engaged, { capture: true, passive: true }));
  }
  // Leaving a page of an ad visit: deepest point seen (% of the page), seconds in view, taps. Sent at every hide with
  // the totals so far; the report keeps the largest.
  const depth = { most: 0, shown: 0, since: document.visibilityState === 'visible' ? performance.now() : null, taps: 0 };
  const measure = () => {
    const page = document.documentElement.scrollHeight || 1;
    depth.most = Math.max(depth.most, Math.min(1, (window.scrollY + window.innerHeight) / page));
  };
  window.addEventListener('scroll', measure, { passive: true });
  window.addEventListener('load', measure);
  window.addEventListener('pointerdown', () => { depth.taps += 1; }, { capture: true, passive: true });
  const leave = () => {
    if (!landingAd && !deep) return;
    if (depth.since !== null) {
      depth.shown += performance.now() - depth.since;
      depth.since = null;
    }
    measure();
    adReport('exit', { sd: String(Math.round(depth.most * 100)), t: String(Math.round(depth.shown / 1000)), n: String(depth.taps),
      x: consentCode() });
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') leave();
    else depth.since = performance.now();
  });
  window.addEventListener('pagehide', () => {
    if (depth.since !== null) leave();
  });
  // Steps of a consenting ad visitor: a product option, the gallery, a video, a product card, a filter, "Devamı".
  document.addEventListener('change', (event) => {
    const field = event.target;
    if (field.closest?.('.collection-filters, [data-filter-panel]')) step('filter', field.name || '');
    else if (field.closest?.('joy-product, .variant-options, form[action*="/cart/add"]')) step('opt', field.name || field.dataset.option || '');
  }, true);
  document.addEventListener('play', (event) => {
    if (event.target.tagName === 'VIDEO') step('vid', location.pathname);
  }, true);
  document.addEventListener('scroll', (event) => {
    if (event.target.closest?.('.gallery-stage, .gallery-thumbnails')) step('gal', location.pathname); // a swipe
  }, { capture: true, passive: true });
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (target.closest?.('[data-collection-more]')) step('more', location.pathname);
    else if (target.closest?.('.gallery, [data-gallery], .gallery-thumbnails, .gallery-stage')) step('gal', location.pathname);
    else {
      const card = target.closest?.('a[href*="/products/"]');
      if (card && !card.closest('joy-product')) {
        try {
          step('card', new URL(card.href).pathname);
        } catch (error) {
          // Not a product address.
        }
      }
    }
  }, true);
  // Capture phase: before the product form's own handler takes the submit over.
  document.addEventListener('submit', (event) => {
    if (event.target.matches?.('form[action*="/cart/add"]')) adReport('cart');
    else if (event.submitter?.name === 'checkout') adReport('checkout');
  }, true);
  // On window, so it runs after the product page has written the customer's choices into the message.
  window.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href*="wa.me/"]');
    if (!link) return;
    let href;
    try {
      href = new URL(link.href);
    } catch (error) {
      return;
    }
    if (!/^\/\d{6,}$/.test(href.pathname)) return; // shared with a friend (wa.me/?text=…), not written to the store
    adReport('wa');
    keepRef();
    let ref = landingRef;
    if (!ref && marketingAllowed()) {
      try {
        ref = sessionStorage.getItem(REF_KEY) || '';
      } catch (error) {
        ref = '';
      }
    }
    const text = href.searchParams.get('text') || '';
    if (ref && !/Ref: [A-Z]$/.test(text)) {
      href.searchParams.set('text', `${text}${text ? '\n\n' : ''}Ref: ${ref}`);
      link.href = href.href;
    }
    const meta = window.ShopifyAnalytics?.meta;
    const variantId = Number(document.querySelector('joy-product [data-variant-result]')?.dataset.variantId || meta?.selectedVariantId || 0);
    const variant = meta?.product?.variants?.find((item) => item.id === variantId);
    const section = link.closest('[id^="shopify-section-"]')?.id.split('__').pop().replace(/^shopify-section-/, '');
    try {
      window.Shopify?.analytics?.publish?.('joy_whatsapp_click', {
        location: link.matches('.whatsapp-float') ? 'float' : section || 'page',
        page_type: meta?.page?.pageType || '',
        product_id: meta?.product?.id || null,
        variant_id: variant?.id || null,
        product_name: variant?.name || '',
        price: variant ? variant.price / 100 : null,
        currency: window.Shopify?.currency?.active || window.JoySettings?.currency,
        ref
      });
    } catch (error) {
      // Analytics must never break the page.
    }
  });
  // Phone calls (owner 2026-10-31: a customer called from Antalya and left no trace anywhere): a press of a tel: link
  // goes to the ad log like a WhatsApp press (no cookies; the landing page, or every page with analytics consent) and
  // is published as joy_phone_click for the "Joy WhatsApp" pixel (GA4 lead, Google Ads "Telefon Tıklama").
  window.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href^="tel:"]');
    if (!link) return;
    adReport('tel');
    let ref = landingRef;
    if (!ref && marketingAllowed()) {
      try {
        ref = sessionStorage.getItem(REF_KEY) || '';
      } catch (error) {
        ref = '';
      }
    }
    const section = link.closest('[id^="shopify-section-"]')?.id.split('__').pop().replace(/^shopify-section-/, '');
    try {
      window.Shopify?.analytics?.publish?.('joy_phone_click', {
        location: section || 'page',
        page_type: window.ShopifyAnalytics?.meta?.page?.pageType || '',
        ref
      });
    } catch (error) {
      // Analytics must never break the page.
    }
  });
  syncWishlist();
  renderWishlistPage();
  // Sister-store bar: shown only to visitors detected in the countries the theme setting lists (Shopify's own
  // browsing-context endpoint); never redirects. Closing it hides it on later visits too.
  const bridgeBar = document.querySelector('[data-bridge-bar]');
  if (bridgeBar) {
    let closed = false;
    try { closed = localStorage.getItem('joy-bridge-bar') === 'closed'; } catch (error) { /* storage blocked */ }
    if (!closed) {
      fetch(`${document.body.dataset.root || '/'}browsing_context_suggestions.json?country[enabled]=true`)
        .then((response) => response.json())
        .then((data) => data?.detected_values?.country?.handle)
        .catch(() => null)
        .then((country) => {
          // Fallback: the privacy API's region ("TR", "USCA") when the endpoint names no country.
          const region = country || window.Shopify?.customerPrivacy?.getRegion?.()?.slice(0, 2);
          if (region && bridgeBar.dataset.countries.split(',').includes(region.toUpperCase())) bridgeBar.hidden = false;
        });
    }
    bridgeBar.querySelector('[data-bridge-close]')?.addEventListener('click', () => {
      bridgeBar.hidden = true;
      try { localStorage.setItem('joy-bridge-bar', 'closed'); } catch (error) { /* storage blocked */ }
    });
  }
  // The opening offer's line under a price (snippets/joy-price.liquid) on a page cached before the offer ended.
  document.querySelectorAll('[data-price-offer]').forEach((line) => {
    if (Number(line.dataset.offerEnds) * 1000 > Date.now()) return;
    line.hidden = true;
    // The taksit line split the offer price: back to the full price.
    const taksit = line.parentElement?.querySelector('[data-taksit-full]');
    if (taksit) taksit.innerHTML = taksit.dataset.taksitFull;
  });
  // Own cookie bar (snippets/joy-consent-bar.liquid, both stores). Shown only where the store requires consent: there
  // analytics starts "not allowed" until the visitor chooses (Settings → Customer privacy regions); elsewhere Shopify
  // allows everything and the bar stays away. The choice goes through Shopify's Customer Privacy API, never straight
  // into cookies. While Shopify's own banner is still on (#shopify-pc__banner), it is left to that banner.
  const consentBar = document.querySelector('[data-consent-bar]');
  const consentPanel = consentBar?.querySelector('[data-consent-panel]');
  let openConsent = null;
  if (consentBar) {
    privacyReady.then((privacy) => {
      if (!privacy) return;
      // The WhatsApp button sits above the bar (--consent-h). The bar grows after it appears: the web font rewraps the
      // text, and on iPhones the home-bar inset joins its bottom padding once Safari's toolbar slides away on scroll
      // (owner 2026-10-23: the button sat on the bar). So the offset follows the bar's live height.
      const setConsentHeight = () => {
        if (!consentBar.hidden) document.documentElement.style.setProperty('--consent-h', `${consentBar.offsetHeight}px`);
      };
      if ('ResizeObserver' in window) new ResizeObserver(setConsentHeight).observe(consentBar, { box: 'border-box' });
      const show = (withPanel) => {
        const choice = privacy.currentVisitorConsent?.() || {};
        ['analytics', 'preferences', 'marketing'].forEach((name) => {
          const box = consentPanel.querySelector(`[name="${name}"]`);
          if (box) box.checked = choice[name] === 'yes';
        });
        consentPanel.hidden = !withPanel;
        consentBar.querySelector('[data-consent-panel-toggle]').setAttribute('aria-expanded', String(withPanel));
        consentBar.hidden = false;
        document.documentElement.classList.add('has-consent-bar');
        setConsentHeight();
      };
      const hide = () => {
        consentBar.hidden = true;
        document.documentElement.classList.remove('has-consent-bar');
      };
      const save = (choice) => privacy.setTrackingConsent(choice, hide);
      openConsent = () => show(true);
      consentBar.addEventListener('click', (event) => {
        const choice = event.target.closest('[data-consent-choice]')?.dataset.consentChoice;
        if (choice) {
          const yes = choice === 'accept';
          save({ analytics: yes, marketing: yes, preferences: yes });
        } else if (event.target.closest('[data-consent-panel-toggle]')) {
          show(consentPanel.hidden);
        }
      });
      consentPanel.addEventListener('submit', (event) => {
        event.preventDefault();
        const ticked = (name) => Boolean(consentPanel.querySelector(`[name="${name}"]`)?.checked);
        save({ analytics: ticked('analytics'), marketing: ticked('marketing'), preferences: ticked('preferences') });
      });
      const choice = privacy.currentVisitorConsent?.() || {};
      const undecided = !choice.analytics && !choice.marketing;
      const required = !privacy.analyticsProcessingAllowed() || !privacy.marketingAllowed();
      // Shopify's banner, if still switched on, renders a moment after load: give it that moment.
      if (undecided && required) setTimeout(() => {
        if (!document.getElementById('shopify-pc__banner')) show(false);
      }, 1200);
    });
  }
  // Footer "Çerez Tercihleri": opens the own bar's choices, or Shopify's banner preferences where that banner runs.
  const cookieLink = document.querySelector('[data-cookie-preferences]');
  if (cookieLink) {
    const reveal = (attempt = 0) => {
      if (openConsent || window.privacyBanner?.showPreferences) cookieLink.hidden = false;
      else if (attempt < 10) setTimeout(() => reveal(attempt + 1), 800);
    };
    reveal();
    cookieLink.addEventListener('click', () => {
      if (openConsent && !document.getElementById('shopify-pc__banner')) openConsent();
      else window.privacyBanner?.showPreferences();
    });
  }
  // Desktop menu on touch screens: joy.css opens panels on hover, which touch lacks, so the first
  // tap on a label opens its panel (.is-open) and a second tap follows the link. A tap outside,
  // on another label, or Escape closes it. Keyboard (:focus-within) and mouse keep the CSS behaviour.
  const noHover = window.matchMedia('(hover: none)');
  const setNavItem = (item, open) => {
    item.classList.toggle('is-open', open);
    const link = item.querySelector(':scope > .nav-link');
    // Mouse desktops also open panels on hover and focus, so they get no collapsed state.
    if (open || noHover.matches) link?.setAttribute('aria-expanded', String(open));
    else link?.removeAttribute('aria-expanded');
  };
  const resetNav = (scope = document) => {
    scope.querySelectorAll('.nav-item.has-panel').forEach((item) => setNavItem(item, false));
  };
  resetNav();
  noHover.addEventListener('change', () => resetNav());
  document.addEventListener('shopify:section:load', (event) => resetNav(event.target));
  // A page restored from the back/forward cache must not come back with a panel open.
  window.addEventListener('pageshow', (event) => { if (event.persisted) resetNav(); });
  document.addEventListener('click', (event) => {
    const open = document.querySelector('.nav-item.is-open');
    if (open && !open.contains(event.target)) setNavItem(open, false);
    const item = event.target.closest('.nav-item.has-panel > .nav-link')?.parentElement;
    if (!item || item.classList.contains('is-open')) return;
    // detail 0 is Enter on a focused link: keyboard users keep following it directly.
    if (event.detail === 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
    // A touch tap on a laptop with a touch screen (hover: hover) needs the same first tap.
    if (!noHover.matches && event.pointerType !== 'touch') return;
    event.preventDefault();
    setNavItem(item, true);
  });

  // Crossing 990px (rotating an iPad, resizing Split View): no desktop menu panel stays open. The filter panel's own
  // handling lives in joy-collection.js (collection pages only).
  window.matchMedia('(min-width: 990px)').addEventListener('change', () => resetNav());
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.mobile-menu[open]').forEach((menu) => { menu.open = false; menu.querySelector('summary')?.focus(); });
    const open = document.querySelector('.nav-item.is-open');
    if (!open) return;
    setNavItem(open, false);
    // Chrome focuses a tapped link, and :focus-within would keep the panel on screen.
    if (open.contains(document.activeElement)) document.activeElement.blur();
  });
  // Mobile menu height (joy.css) starts at the panel's real top edge, which moves with the
  // announcement bar (one or two lines), so measure it on open and on resize while open.
  const setMenuTop = () => {
    const panel = document.querySelector('.mobile-menu[open] .mobile-menu-panel');
    if (panel) document.documentElement.style.setProperty('--menu-top', `${Math.max(0, Math.round(panel.getBoundingClientRect().top))}px`);
  };
  // toggle does not bubble, so listen in the capture phase.
  let menuOpen = false;
  document.addEventListener('toggle', (event) => {
    if (!event.target.matches?.('.mobile-menu')) return;
    menuOpen = event.target.open;
    if (event.target.open) setMenuTop();
    // The button reads "Menüyü kapat" while the menu is open (its icon turns into ✕).
    const toggle = event.target.querySelector('summary');
    if (toggle?.dataset.labelClose) toggle.setAttribute('aria-label', event.target.open ? toggle.dataset.labelClose : toggle.dataset.labelOpen);
  }, true);
  window.addEventListener('resize', setMenuTop);
  // The header sticks: scrolling with the menu open moves the panel's top (the announcement bar scrolls away).
  window.addEventListener('scroll', () => { if (menuOpen) setMenuTop(); }, { passive: true });
  // Sticky header height for the CSS (scroll padding, sticky columns and sidebars below it).
  const siteHeader = document.querySelector('.site-header');
  if (siteHeader) {
    const setHeaderHeight = () => document.documentElement.style.setProperty('--header-height', `${siteHeader.offsetHeight}px`);
    setHeaderHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeaderHeight).observe(siteHeader);
    else window.addEventListener('resize', setHeaderHeight);
  }
})();
